import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import APRmemo from "./APRComment.js/APRmemo";
import APRComment from "./APRComment.js/AprComments";
import {
  filterTrackedEntitiesByCreatedAt,
  getAttributeValue,
  getDataByTypes,
  getProjectDetails,
  groupProjectsBySectorAmountWithBudget,
} from "../../utils/utils";
import axios from "../../api/axios";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const PROJECT_TYPE_ATTRIBUTE = "wtf3BR2YO8w";
const PROJECT_TYPE_VALUE = "Project";
const BUDGET_PROGRAM = "YHVtzXj8iIC";

const SECTORS = [
  "Health",
  "Education",
  "Governance/Administration",
  "Water and Sanitation",
  "Roads and Transport",
  "Trade, Industry and Tourism",
  "Security",
];

const Table_14 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);
  const [showChart, setShowChart] = useState(false);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getData();
  }, [district, selectedYear, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_14",
      tableData,
    }),
  }));

  async function getData() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;

      const [projectsResp, eventsResp, budgetResp] = await Promise.all([
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${PROJECT_PROGRAM}&filter=${PROJECT_TYPE_ATTRIBUTE}:EQ:${PROJECT_TYPE_VALUE}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/events?program=${PROJECT_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${BUDGET_PROGRAM}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
      ]);

      const projects = projectsResp.data.instances || [];
      const reports = eventsResp.data.instances || [];
      const budgets = filterTrackedEntitiesByCreatedAt(budgetResp.data.instances || [], selectedYear, period);

      const projectDetails = getProjectDetails(projects, reports);
      const budgetBySectors = getDataByTypes(budgets, "Sector", SECTORS) || [];
      const tempBudget = budgetBySectors.map((budget) => ({
        sector: getAttributeValue("Sector", budget),
        budget: getAttributeValue("Allocated Budget", budget),
      }));

      setTableData(
        groupProjectsBySectorAmountWithBudget(projectDetails, SECTORS, tempBudget, selectedYear),
      );
    } catch (err) {
      console.error("Error loading Table 14 data:", err);
      setTableData([]);
    }
  }

  const chartRows = tableData.filter((row) => row.sector !== "Total");

  const chartData = {
    labels: chartRows.map((row) => row.sector),
    datasets: [
      {
        label: "Capital Envelope Amount",
        data: chartRows.map((row) => row.capitalEnvelop || 0),
        backgroundColor: "#007bff",
        borderColor: "#0056b3",
        borderWidth: 1,
      },
      {
        label: "Amount Spent on Rollover Projects",
        data: chartRows.map((row) => row.rolloverCost || 0),
        backgroundColor: "#ff9800",
        borderColor: "#e07a00",
        borderWidth: 1,
      },
      {
        label: "Amount Spent on New Projects",
        data: chartRows.map((row) => row.newCost || 0),
        backgroundColor: "#28a745",
        borderColor: "#1e7e34",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Capital Envelope Spending Analysis, ${selectedYear}`,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${new Intl.NumberFormat("en-GH", {
            style: "currency",
            currency: "GHS",
            minimumFractionDigits: 2,
          }).format(context.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Amount (GH¢)",
        },
        ticks: {
          callback: (value) => new Intl.NumberFormat("en-GH", {
            style: "currency",
            currency: "GHS",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(value),
        },
      },
    },
  };

  const formatAmount = (value) => (value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="col-12">
      <h3>Table 14: Amount of capital envelope spent on active projects</h3>
      <div className="card">
        <div className="card-header" />
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table14-${year}`}
            hideTableDis={hideTableDis}
          />
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead style={{ backgroundColor: "#d4edda", fontWeight: "bold", border: "1px solid #000" }}>
                <tr>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Sector</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Capital envelope amount</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Amount spent on rollover projects</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Amount spent on new projects</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #000" }}>{row?.sector}</td>
                      <td style={{ border: "1px solid #000" }}>{formatAmount(row?.capitalEnvelop)}</td>
                      <td style={{ border: "1px solid #000" }}>{formatAmount(row?.rolloverCost)}</td>
                      <td style={{ border: "1px solid #000" }}>{formatAmount(row?.newCost)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ border: "1px solid #000", textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2">
            <small>Source: Payment Vouchers & Trial Balance, {selectedYear}</small>
          </p>

          <button className="btn btn-primary mt-3 no-print" onClick={() => setShowChart(!showChart)}>
            {showChart ? "Hide chart" : "Show optional chart"}
          </button>
          {showChart && (
            <div style={{ height: "420px" }} className="mt-3">
              {chartRows.length > 0 ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <p className="text-center">No chart data available for {selectedYear}</p>
              )}
            </div>
          )}

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table14-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_14;
